import React, { useEffect, useRef, useState } from "react";
import BN from "bn.js";
import { Keypair, PublicKey, SystemProgram } from "@solana/web3.js";
import {
  ACCOUNT_SIZE,
  AuthorityType,
  MINT_SIZE,
  NATIVE_MINT,
  TOKEN_PROGRAM_ID,
  createAssociatedTokenAccountInstruction,
  createInitializeMint2Instruction,
  createMintToInstruction,
  createSetAuthorityInstruction,
  getMinimumBalanceForRentExemptMint,
} from "@solana/spl-token";
import { TxVersion } from "@raydium-io/raydium-sdk-v2";
import {
  OnChainFeeTier,
  cpmmPrograms,
  describeTier,
  pct,
  readFeeTiers,
} from "@oxminter/shared/feeconfig";
import { fromBaseUnits, openingPrice, toBaseUnits } from "@oxminter/shared/amounts";
import {
  blockingDifferences,
  buildRecord,
  carryToCluster,
  diffAgainstRecord,
  parseRecord,
  recordFilename,
  type CarryResult,
  type PlanDifference,
  type RehearsalPlan,
  type RehearsalRecord,
} from "@oxminter/shared/rehearsal";
import {
  OPEN_IMMEDIATELY,
  describeOpenTime,
  localInputToUnix,
  unixToLocalInput,
  validateOpenTime,
} from "@oxminter/shared/schedule";
import { useApp } from "../state";
import type { LaunchSnapshot } from "../lib/guide";
import { Addr, Field, Note, Req, RequireWallet, Stat } from "../components/common";
import { buildSimulateSend, SimulationError } from "../lib/tx";
import { ataFor, readMintFacts } from "../lib/token";
import { buildCreateMetadataIx, probeMetadataUri, readMetadata } from "../lib/metadata";
import { autoFillUri, buildMetadataDoc, metadataSlug, metadataUriFor } from "../lib/metadata-doc";
import { CoinImage } from "../components/CoinImage";
import { getPoolInfoWithRetry, loadRaydium } from "../lib/raydium";
import { isMixedContentRpc } from "../lib/network";
import {
  apiConfigured,
  getCloudRecord,
  listCloudRecords,
  recordEvent as postAuditEvent,
  saveCloudRecord,
  whoami,
  type CloudRecordSummary,
} from "../lib/api";

const STAGES = [
  "CREATE_MINT",
  "CREATE_METADATA",
  "VERIFY_TOKEN",
  "SET_AUTHORITIES",
  "VERIFY_FINAL",
  "CREATE_POOL",
  "VERIFY_POOL",
] as const;
type Stage = (typeof STAGES)[number];

/* Two real pipelines, not one pipeline with fields the user is told to ignore.
   A token can exist without a pool; the pool fields are only reachable
   requirements when the run is going to open one. VERIFY_FINAL exists so the
   token-only path still reads the post-revocation authority state back off the
   chain — that check lives inside VERIFY_POOL on the pool path. */
const TOKEN_ONLY_STAGES: Stage[] = [
  "CREATE_MINT",
  "CREATE_METADATA",
  "VERIFY_TOKEN",
  "SET_AUTHORITIES",
  "VERIFY_FINAL",
];
const WITH_POOL_STAGES: Stage[] = [
  "CREATE_MINT",
  "CREATE_METADATA",
  "VERIFY_TOKEN",
  "SET_AUTHORITIES",
  "CREATE_POOL",
  "VERIFY_POOL",
];

interface Journal {
  mint?: string;
  mintSecret?: number[];
  done: Stage[];
  sigs: Record<string, string>;
  poolId?: string;
  lpMint?: string;
  /**
   * Captured the moment the launch finishes, from the values just read back off
   * the chain — never rebuilt later from the form. Switching tabs unmounts this
   * panel and resets its form state, so a record built on demand would be full
   * of blanks and would not say so.
   */
  rehearsalRecord?: RehearsalRecord;
}

const emptyJournal: Journal = { done: [], sigs: {} };

function journalKey(rpcUrl: string) {
  return `launch-journal:${rpcUrl}`;
}

function loadJournal(rpcUrl: string): Journal {
  try {
    const raw = localStorage.getItem(journalKey(rpcUrl));
    return raw ? (JSON.parse(raw) as Journal) : { ...emptyJournal };
  } catch {
    return { ...emptyJournal };
  }
}

function saveJournal(rpcUrl: string, j: Journal) {
  try {
    localStorage.setItem(journalKey(rpcUrl), JSON.stringify(j));
  } catch {
    /* private browsing; the run still works, it just cannot resume */
  }
}

interface Form {
  name: string;
  symbol: string;
  decimals: number;
  totalSupply: string;
  metadataUri: string;
  /** Optional. Empty falls back to the console's own logo when building the doc. */
  imageUrl: string;
  mutableMetadata: boolean;
  revokeMint: boolean;
  revokeFreeze: boolean;
  tokenLiquidity: string;
  solLiquidity: string;
  feeTierId: string;
  openPool: boolean;
  /** "" means open immediately; otherwise a datetime-local value. */
  openAt: string;
}

const DEFAULT_FORM: Form = {
  name: "",
  symbol: "",
  decimals: 9,
  totalSupply: "1000000000",
  metadataUri: "",
  imageUrl: "",
  mutableMetadata: true,
  revokeMint: true,
  revokeFreeze: true,
  tokenLiquidity: "1000000",
  solLiquidity: "0.5",
  feeTierId: "",
  openPool: true,
  openAt: "",
};

export default function Launch() {
  const { connection, net, rpcUrl, wallet, fees, say, setToken, setLaunchStatus } = useApp();
  const [form, setForm] = useState<Form>(DEFAULT_FORM);
  const [tiers, setTiers] = useState<OnChainFeeTier[]>([]);
  const [tiersError, setTiersError] = useState<string | null>(null);
  const [journal, setJournal] = useState<Journal>(() => loadJournal(rpcUrl));
  const [running, setRunning] = useState(false);
  const [current, setCurrent] = useState<Stage | null>(null);
  const [failed, setFailed] = useState<Stage | null>(null);
  const [uriProbe, setUriProbe] = useState<{ ok: boolean; detail: string } | null>(null);
  const [checks, setChecks] = useState<{ name: string; expected: string; actual: string; pass: boolean }[]>([]);
  const [balance, setBalance] = useState<number | null>(null);
  // Rent-exempt lamports for the accounts a token-only run creates. Read from
  // the chain rather than hardcoded, because the rent rate is a cluster
  // parameter and a stale constant would quietly under-fund the estimate.
  const [rent, setRent] = useState<number | null>(null);
  /* The validator's own clock. getBlockTime on the latest slot is what the
     cp-swap program will compare against; the browser's clock is not. */
  const [clusterNowSec, setClusterNowSec] = useState<number | null>(null);

  /*
    The metadata URI is filled in on devnet so a rehearsal is not blocked by
    hosting a JSON file first, and CLEARED the moment the cluster is mainnet.

    The clear is the important half. The console ships exactly one ready-made
    document and it describes a rehearsal token; carrying it into a mainnet
    launch would mint a real, irreversible token called "oxMinter Devnet
    Rehearsal" with a placeholder image, and metadata is written before
    authorities are revoked, so it could not be corrected afterwards. Only a
    value this component filled in is ever removed -- anything typed is left
    alone, because silently deleting an operator's own input is its own bug.
  */
  /*
    Whether the URI was filled in by this component is derived from a ref
    holding the exact value written, compared inside setForm's updater where
    the form is guaranteed fresh.

    A boolean useState was tried first and was wrong: the effect reads state
    captured at the render it was created in, so after the operator typed their
    own URI and switched clusters, a stale read marked their value as
    auto-filled and the next switch to mainnet DELETED IT. The browser test
    caught it; instrumenting showed the notice turning on over a hand-typed
    URI. Comparing against the value itself cannot go stale.
  */
  const autoFilledRef = useRef<string | null>(null);
  useEffect(() => {
    const suggested = autoFillUri(net.cluster, window.location.origin);
    setForm((f) => {
      const wasAuto = autoFilledRef.current !== null && f.metadataUri === autoFilledRef.current;
      if (suggested === null) {
        // Mainnet, or an origin whose URI the checklist would reject. Only a
        // value this component wrote is removed; anything typed is left alone.
        if (!wasAuto) return f;
        autoFilledRef.current = null;
        return { ...f, metadataUri: "" };
      }
      if (f.metadataUri === "" || wasAuto) {
        autoFilledRef.current = suggested;
        return { ...f, metadataUri: suggested };
      }
      return f;
    });
    // Outside the updater, deliberately. A setState updater must be pure --
    // React may invoke it more than once, and calling another setState from
    // inside it is undefined behaviour that shows up as re-render churn
    // elsewhere in the panel. A probe result from a different cluster is
    // meaningless either way, so clearing it unconditionally is correct.
    setUriProbe(null);
  }, [net.cluster]);

  const uriAutoFilled = form.metadataUri !== "" && form.metadataUri === autoFilledRef.current;

  function downloadMetadataDoc() {
    const doc = buildMetadataDoc(
      { name: form.name, symbol: form.symbol, description: "", image: form.imageUrl },
      window.location.origin
    );
    const slug = metadataSlug(form.name) || "token";
    const blob = new Blob([JSON.stringify(doc, null, 2) + "\n"], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${slug}.json`;
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      URL.revokeObjectURL(url);
      a.remove();
    }, 30_000);
    say(
      "info",
      `Saved ${slug}.json. Put it in console/public/meta/, redeploy, and its URI is ` +
        `${metadataUriFor(window.location.origin, slug)}`
    );
  }
  /* A rehearsal loaded from another cluster. Kept for the whole session so the
     plan can be re-compared after every edit, not only at import. */
  const [rehearsal, setRehearsal] = useState<RehearsalRecord | null>(null);
  const [carry, setCarry] = useState<CarryResult | null>(null);
  const [carryError, setCarryError] = useState<string | null>(null);
  const [acknowledgedDrift, setAcknowledgedDrift] = useState(false);
  const [cloudRecords, setCloudRecords] = useState<CloudRecordSummary[]>([]);
  const [signedIn, setSignedIn] = useState(false);

  useEffect(() => {
    if (!apiConfigured) return;
    let live = true;
    void whoami().then((a) => {
      if (!live) return;
      setSignedIn(a.authenticated);
      if (a.authenticated) void listCloudRecords().then((r) => live && setCloudRecords(r));
    });
    return () => {
      live = false;
    };
  }, []);

  useEffect(() => setJournal(loadJournal(rpcUrl)), [rpcUrl]);

  useEffect(() => {
    let live = true;
    setTiers([]);
    setTiersError(null);
    readFeeTiers(connection, net.cluster)
      .then((t) => {
        if (!live) return;
        setTiers(t);
        if (t.length === 0) {
          setTiersError(
            "No CPMM fee-tier accounts found at this RPC endpoint. Wrong network, or the node is unreachable."
          );
        }
      })
      .catch((e) => {
        if (!live) return;
        /*
          "Failed to fetch" is what the browser says when it blocked the
          request itself, and it names neither the cause nor the fix. The
          common case by far is an https page reaching for an http RPC —
          which is what a deployed console does if it is left on Localnet.
        */
        const raw = (e as Error).message;
        setTiersError(
          isMixedContentRpc(rpcUrl, window.location.protocol)
            ? `${raw} — this page is https and the RPC is ${rpcUrl}, which the browser blocks as mixed content. Pick Devnet in the network selector.`
            : raw
        );
      });
    return () => {
      live = false;
    };
  }, [connection, net.cluster]);

  useEffect(() => {
    if (!wallet) return setBalance(null);
    let live = true;
    connection
      .getBalance(wallet.publicKey, "confirmed")
      .then((b) => live && setBalance(b))
      .catch(() => live && setBalance(null));
    return () => {
      live = false;
    };
  }, [wallet, connection, journal]);

  useEffect(() => {
    let live = true;
    const tick = () =>
      connection
        .getSlot("confirmed")
        .then((slot) => connection.getBlockTime(slot))
        .then((t) => live && t && setClusterNowSec(t))
        .catch(() => live && setClusterNowSec(null));
    void tick();
    const id = setInterval(tick, 20000);
    return () => {
      live = false;
      clearInterval(id);
    };
  }, [connection]);

  useEffect(() => {
    let live = true;
    Promise.all([
      connection.getMinimumBalanceForRentExemption(MINT_SIZE),
      connection.getMinimumBalanceForRentExemption(ACCOUNT_SIZE),
    ])
      .then(([mintRent, ataRent]) => live && setRent(mintRent + ataRent))
      .catch(() => live && setRent(null));
    return () => {
      live = false;
    };
  }, [connection]);

  const tier: OnChainFeeTier | null = (() => {
    if (!form.feeTierId) return null;
    return tiers.find((t) => t.id === form.feeTierId) ?? null;
  })();

  const set = <K extends keyof Form>(k: K, v: Form[K]) => setForm((f) => ({ ...f, [k]: v }));

  // ---- validation -------------------------------------------------------
  // Two buckets, because they gate different outcomes. Anything in
  // `mintProblems` prevents a token existing at all; anything in
  // `poolProblems` only matters when this run is also opening a pool.
  const mintProblems: string[] = [];
  const poolProblems: string[] = [];

  if (!form.name || form.name.length > 32) mintProblems.push("Name must be 1–32 characters.");
  if (!form.symbol || form.symbol.length > 10) mintProblems.push("Symbol must be 1–10 characters.");
  if (!Number.isInteger(form.decimals) || form.decimals < 0 || form.decimals > 9)
    mintProblems.push("Decimals must be a whole number from 0 to 9.");
  if (form.metadataUri.length > 200)
    mintProblems.push("Metadata URI must be 200 characters or fewer.");
  if (!/^(https:\/\/|ipfs:\/\/|ar:\/\/)/.test(form.metadataUri))
    mintProblems.push("Metadata URI must start with https://, ipfs:// or ar://.");
  if (form.openPool && !tier) poolProblems.push("Select a fee tier.");

  let supplyRaw: BN | null = null;
  try {
    supplyRaw = toBaseUnits(form.totalSupply, form.decimals);
    if (supplyRaw.isZero()) mintProblems.push("Total supply must be greater than zero.");
  } catch (e) {
    mintProblems.push((e as Error).message);
  }

  let tokenLiqRaw: BN | null = null;
  let solLiqRaw: BN | null = null;
  try {
    tokenLiqRaw = toBaseUnits(form.tokenLiquidity, form.decimals);
    solLiqRaw = toBaseUnits(form.solLiquidity, 9);
    if (tokenLiqRaw.isZero()) poolProblems.push("Initial token liquidity must be greater than zero.");
    if (solLiqRaw.isZero()) poolProblems.push("Initial SOL liquidity must be greater than zero.");
    if (supplyRaw && tokenLiqRaw.gt(supplyRaw)) poolProblems.push("Pooled amount exceeds total supply.");
  } catch (e) {
    poolProblems.push((e as Error).message);
  }

  /* The schedule is validated against the CLUSTER's clock, not the browser's.
     The program compares open_time to the on-chain clock, and a machine whose
     time is a few minutes out would otherwise pass a check the chain fails. */
  const nowSec = clusterNowSec ?? Math.floor(Date.now() / 1000);
  const schedule = form.openAt
    ? validateOpenTime(localInputToUnix(form.openAt), nowSec)
    : { unix: OPEN_IMMEDIATELY, problems: [] };
  if (form.openPool) {
    schedule.problems.filter((p) => p.fatal).forEach((p) => poolProblems.push(p.message));
  }
  const scheduleWarnings = form.openPool ? schedule.problems.filter((p) => !p.fatal) : [];

  /* The plan as the rehearsal record describes one, so the two can be compared
     field by field rather than by eye. */
  const currentPlan: RehearsalPlan = {
    name: form.name,
    symbol: form.symbol,
    decimals: form.decimals,
    totalSupply: form.totalSupply,
    mutableMetadata: form.mutableMetadata,
    revokeMint: form.revokeMint,
    revokeFreeze: form.revokeFreeze,
    openPool: form.openPool,
    tokenLiquidity: form.tokenLiquidity,
    solLiquidity: form.solLiquidity,
  };
  const drift: PlanDifference[] = rehearsal ? diffAgainstRecord(rehearsal, currentPlan) : [];
  const blocking = blockingDifferences(drift);
  /* Identity drift from a loaded rehearsal blocks the launch. It can only be
     released by ticking the acknowledgement, which is a deliberate act rather
     than a click-through — the whole value of the record is that "this is what
     I tested" stops being a memory. */
  if (blocking.length && !acknowledgedDrift) {
    mintProblems.push(
      `${blocking.length} field${blocking.length === 1 ? "" : "s"} differ from the loaded ` +
        `rehearsal. Review them below, or acknowledge the change.`
    );
  }

  const problems = form.openPool ? [...mintProblems, ...poolProblems] : mintProblems;

  // The smallest set of inputs that produces a token, shown live so "required"
  // is something you can watch turn green rather than a word next to a label.
  const mintChecklist: { label: string; met: boolean; why: string; pending?: boolean }[] = [
    { label: "Token name", met: !!form.name && form.name.length <= 32, why: "1–32 characters, written into the Metaplex metadata account." },
    { label: "Symbol", met: !!form.symbol && form.symbol.length <= 10, why: "1–10 characters. What wallets and explorers display." },
    { label: "Decimals", met: Number.isInteger(form.decimals) && form.decimals >= 0 && form.decimals <= 9, why: "Fixed at creation. Cannot be changed afterwards." },
    { label: "Total supply", met: !!supplyRaw && !supplyRaw.isZero(), why: "Minted once, in full, to your wallet in the same transaction." },
    { label: "Metadata JSON URI", met: /^(https:\/\/|ipfs:\/\/|ar:\/\/)/.test(form.metadataUri) && form.metadataUri.length <= 200, why: "Without it the token has no name or image anywhere off-chain." },
    // `pending` rather than unmet while the balance read is in flight. A
    // requirement shown as failed because an RPC round trip has not returned is
    // a false alarm, and this is the one row that depends on the network.
    {
      label: "A connected wallet with SOL",
      met: balance !== null && balance > 0,
      pending: balance === null,
      why: "Pays rent and fees, receives the entire supply, and signs every step.",
    },
  ];

  const price =
    tokenLiqRaw && solLiqRaw && !tokenLiqRaw.isZero()
      ? openingPrice(tokenLiqRaw, form.decimals, solLiqRaw, 9)
      : "—";

  // Pool path: the pool-creation fee, the SOL you are depositing, and headroom
  // for rent and priority fees. Token-only path: measured rent for the mint and
  // the associated token account, plus a margin covering the Metaplex metadata
  // account (whose size the program chooses, so it is not measurable from here)
  // and transaction fees. An over-estimate, deliberately — under-funding is how
  // a run dies between the mint and the metadata.
  const requiredLamports = form.openPool
    ? tier && solLiqRaw
      ? new BN(tier.createPoolFeeLamports).add(solLiqRaw).add(new BN(80_000_000))
      : null
    : rent !== null
      ? new BN(rent).add(new BN(15_000_000))
      : null;
  const underfunded =
    requiredLamports !== null && balance !== null && new BN(balance).lt(requiredLamports);

  const isDone = (s: Stage) => journal.done.includes(s);

  const record = (patch: Partial<Journal>, stage?: Stage, sig?: string) => {
    setJournal((prev) => {
      const next: Journal = {
        ...prev,
        ...patch,
        done: stage && !prev.done.includes(stage) ? [...prev.done, stage] : prev.done,
        sigs: sig && stage ? { ...prev.sigs, [stage]: sig } : prev.sigs,
      };
      saveJournal(rpcUrl, next);
      return next;
    });
  };

  const fail = (stage: Stage, e: unknown) => {
    setFailed(stage);
    const err = e as Error & { logs?: string[] };
    say("error", `${stage}: ${err.message}`);
    if (err instanceof SimulationError) err.logs.slice(-8).forEach((l) => say("error", `  ${l}`));
  };

  // ---- the run ----------------------------------------------------------
  async function run() {
    if (!wallet || !supplyRaw) return;
    // Bundled so the pool stages can rely on all three being present at once;
    // three separate nullable locals would need three redundant assertions.
    const poolPlan =
      form.openPool && tier && tokenLiqRaw && solLiqRaw
        ? { tier, tokenLiqRaw, solLiqRaw }
        : null;
    if (form.openPool && !poolPlan) return;
    setRunning(true);
    setFailed(null);
    setChecks([]);

    // `current` is React state, so inside this async function it stays frozen at
    // the value from the render that started the run. Error reporting has to
    // track the stage locally or every failure gets blamed on the first stage.
    let stage: Stage = "CREATE_MINT";
    const enter = (s: Stage) => {
      stage = s;
      setCurrent(s);
    };

    try {
      // -- mint keypair is persisted BEFORE it is used, so an interrupted run
      //    resumes onto the same mint instead of creating a second token.
      let mintKp: Keypair;
      if (journal.mintSecret) {
        mintKp = Keypair.fromSecretKey(Uint8Array.from(journal.mintSecret));
      } else {
        mintKp = Keypair.generate();
        record({ mint: mintKp.publicKey.toBase58(), mintSecret: [...mintKp.secretKey] });
      }
      const mint = mintKp.publicKey;

      // ---------------- CREATE_MINT ----------------
      if (!isDone("CREATE_MINT")) {
        enter("CREATE_MINT");
        say("info", `Creating mint ${mint.toBase58()}`);
        const rent = await getMinimumBalanceForRentExemptMint(connection);
        const ata = ataFor(mint, wallet.publicKey);
        const { signature } = await buildSimulateSend({
          connection,
          wallet,
          fees,
          extraSigners: [mintKp],
          label: "Create mint",
          onProgress: (m) => say("info", m),
          instructions: [
            SystemProgram.createAccount({
              fromPubkey: wallet.publicKey,
              newAccountPubkey: mint,
              space: MINT_SIZE,
              lamports: rent,
              programId: TOKEN_PROGRAM_ID,
            }),
            createInitializeMint2Instruction(
              mint,
              form.decimals,
              wallet.publicKey,
              wallet.publicKey,
              TOKEN_PROGRAM_ID
            ),
            createAssociatedTokenAccountInstruction(
              wallet.publicKey,
              ata,
              wallet.publicKey,
              mint,
              TOKEN_PROGRAM_ID
            ),
            createMintToInstruction(
              mint,
              ata,
              wallet.publicKey,
              BigInt(supplyRaw.toString()),
              [],
              TOKEN_PROGRAM_ID
            ),
          ],
        });
        record({}, "CREATE_MINT", signature);
        say("ok", `Mint created and ${form.totalSupply} ${form.symbol} minted`);
      }

      // ---------------- CREATE_METADATA ----------------
      if (!isDone("CREATE_METADATA")) {
        enter("CREATE_METADATA");
        const existing = await readMetadata(rpcUrl, mint);
        if (existing) {
          record({}, "CREATE_METADATA");
          say("ok", "Metadata account already exists");
        } else {
          const { signature } = await buildSimulateSend({
            connection,
            wallet,
            fees,
            label: "Create metadata",
            onProgress: (m) => say("info", m),
            instructions: buildCreateMetadataIx(rpcUrl, mint, wallet.publicKey, {
              name: form.name,
              symbol: form.symbol,
              uri: form.metadataUri,
              sellerFeeBasisPoints: 0,
              mutable: form.mutableMetadata,
            }),
          });
          record({}, "CREATE_METADATA", signature);
          say("ok", "Metadata created");
        }
      }

      // ---------------- VERIFY_TOKEN ----------------
      enter("VERIFY_TOKEN");
      const facts = await readMintFacts(connection, mint);
      const md = await readMetadata(rpcUrl, mint);
      const authoritiesDone = isDone("SET_AUTHORITIES");
      const tokenChecks = [
        c("mint decimals", form.decimals, facts.decimals),
        c("mint supply (raw)", supplyRaw.toString(), facts.supplyRaw),
        c(
          authoritiesDone ? "mint authority (post-revoke)" : "mint authority (pre-revoke)",
          authoritiesDone && form.revokeMint ? "null" : wallet.publicKey.toBase58(),
          String(facts.mintAuthority)
        ),
        c("metadata name", form.name, md?.name ?? "missing"),
        c("metadata symbol", form.symbol, md?.symbol ?? "missing"),
        c("metadata uri", form.metadataUri, md?.uri ?? "missing"),
        c("metadata mutable", form.mutableMetadata, md?.isMutable ?? "missing"),
      ];
      setChecks(tokenChecks);
      if (tokenChecks.some((x) => !x.pass)) {
        throw new Error(
          form.openPool
            ? "Token verification failed; stopping before the pool is created."
            : "Token verification failed; stopping before any authority is revoked."
        );
      }
      record({}, "VERIFY_TOKEN");
      say("ok", `Token verification passed (${tokenChecks.length}/${tokenChecks.length})`);

      // ---------------- SET_AUTHORITIES ----------------
      if (!isDone("SET_AUTHORITIES")) {
        enter("SET_AUTHORITIES");
        if (!form.revokeMint && !form.revokeFreeze) {
          record({}, "SET_AUTHORITIES");
          say("warn", "Neither authority revoked — both remain with your wallet");
        } else {
          const ixs = [];
          if (form.revokeMint)
            ixs.push(
              createSetAuthorityInstruction(
                mint,
                wallet.publicKey,
                AuthorityType.MintTokens,
                null,
                [],
                TOKEN_PROGRAM_ID
              )
            );
          if (form.revokeFreeze)
            ixs.push(
              createSetAuthorityInstruction(
                mint,
                wallet.publicKey,
                AuthorityType.FreezeAccount,
                null,
                [],
                TOKEN_PROGRAM_ID
              )
            );
          const { signature } = await buildSimulateSend({
            connection,
            wallet,
            fees,
            label: "Revoke authorities",
            onProgress: (m) => say("info", m),
            instructions: ixs,
          });
          record({}, "SET_AUTHORITIES", signature);
          say("ok", "Authorities revoked (irreversible)");
        }
      }

      // ---------------- VERIFY_FINAL (token-only runs) ----------------
      // The pool path proves the post-revocation authority state inside
      // VERIFY_POOL. Without a pool there is nowhere else for that proof to
      // live, and "revoked" is exactly the claim nobody should take on trust.
      if (!form.openPool) {
        enter("VERIFY_FINAL");
        const finalMint = await readMintFacts(connection, mint);
        const finalChecks = [
          c(
            "mint authority after finalize",
            form.revokeMint ? "null" : wallet.publicKey.toBase58(),
            String(finalMint.mintAuthority)
          ),
          c(
            "freeze authority after finalize",
            form.revokeFreeze ? "null" : wallet.publicKey.toBase58(),
            String(finalMint.freezeAuthority)
          ),
          c("supply unchanged by finalize", supplyRaw.toString(), finalMint.supplyRaw),
        ];
        setChecks([...tokenChecks, ...finalChecks]);
        if (finalChecks.some((x) => !x.pass)) throw new Error("Final token verification failed.");
        record(
          {
            rehearsalRecord: captureRecord(mint.toBase58(), [...tokenChecks, ...finalChecks]),
          },
          "VERIFY_FINAL"
        );
        say("ok", `Final verification passed (${finalChecks.length}/${finalChecks.length})`);

        setToken({ mint: mint.toBase58(), symbol: form.symbol, decimals: form.decimals });
        setCurrent(null);
        say(
          "warn",
          "Token created without a pool. Nobody can buy or sell it until liquidity exists."
        );
        return;
      }

      // Past the token-only return, so the plan must exist. Asserting rather
      // than non-null-asserting each use keeps the failure loud if that ever
      // stops being true.
      if (!poolPlan) throw new Error("internal: pool stages reached without a pool plan");

      // ---------------- CREATE_POOL ----------------
      let poolId = journal.poolId;
      let lpMint = journal.lpMint;
      if (!isDone("CREATE_POOL")) {
        enter("CREATE_POOL");
        const raydium = await loadRaydium(connection, net.cluster, wallet);
        const programs = cpmmPrograms(net.cluster);
        const { execute, extInfo } = await raydium.cpmm.createPool({
          programId: programs.programId,
          poolFeeAccount: programs.poolFeeAccount,
          mintA: {
            address: mint.toBase58(),
            decimals: form.decimals,
            programId: TOKEN_PROGRAM_ID.toBase58(),
          },
          mintB: {
            address: NATIVE_MINT.toBase58(),
            decimals: 9,
            programId: TOKEN_PROGRAM_ID.toBase58(),
          },
          mintAAmount: poolPlan.tokenLiqRaw,
          mintBAmount: poolPlan.solLiqRaw,
          startTime: new BN(schedule.unix),
          feeConfig: poolPlan.tier,
          associatedOnly: false,
          ownerInfo: { useSOLBalance: true },
          txVersion: TxVersion.V0,
          computeBudgetConfig: {
            units: fees.computeUnitLimit,
            microLamports: fees.computeUnitPriceMicroLamports,
          },
        });
        poolId = extInfo.address.poolId.toBase58();
        lpMint = extInfo.address.lpMint.toBase58();
        say("info", "Creating pool: awaiting wallet approval");
        const { txId } = await execute({ sendAndConfirm: true, skipPreflight: false });
        record({ poolId, lpMint }, "CREATE_POOL", txId);
        say("ok", `Pool ${poolId} created`);
      }

      // ---------------- VERIFY_POOL ----------------
      enter("VERIFY_POOL");
      const raydium = await loadRaydium(connection, net.cluster, wallet);
      const programs = cpmmPrograms(net.cluster);
      const d = await getPoolInfoWithRetry(() => raydium.cpmm.getRpcPoolInfo(poolId!, true));
      const finalMint = await readMintFacts(connection, mint);
      const tokenIsA = d.mintA.toBase58() === mint.toBase58();
      const poolChecks = [
        c("pool owned by CPMM program", programs.programId.toBase58(), d.programId.toBase58()),
        c(
          "pool pairs the mint with WSOL",
          true,
          (tokenIsA ? d.mintB : d.mintA).toBase58() === NATIVE_MINT.toBase58()
        ),
        c("pool fee tier account", poolPlan.tier.id, d.configId.toBase58()),
        c("pool trade fee rate", String(poolPlan.tier.tradeFeeRate), d.configInfo?.tradeFeeRate.toString() ?? "?"),
        c("pool creator is your wallet", wallet.publicKey.toBase58(), d.poolCreator.toBase58()),
        c(
          "token reserve (raw)",
          poolPlan.tokenLiqRaw.toString(),
          (tokenIsA ? d.baseReserve : d.quoteReserve).toString()
        ),
        c(
          "SOL reserve (raw)",
          poolPlan.solLiqRaw.toString(),
          (tokenIsA ? d.quoteReserve : d.baseReserve).toString()
        ),
        c("lp mint matches creation output", lpMint!, d.mintLp.toBase58()),
        // The whole point of a schedule is that it is on chain rather than a
        // promise, so it is read back like everything else.
        c("pool open time", String(schedule.unix), d.openTime.toString()),
        c(
          "mint authority after finalize",
          form.revokeMint ? "null" : wallet.publicKey.toBase58(),
          String(finalMint.mintAuthority)
        ),
        c(
          "freeze authority after finalize",
          form.revokeFreeze ? "null" : wallet.publicKey.toBase58(),
          String(finalMint.freezeAuthority)
        ),
      ];
      setChecks([...tokenChecks, ...poolChecks]);
      if (poolChecks.some((x) => !x.pass)) throw new Error("Pool verification failed.");
      record(
        {
          rehearsalRecord: captureRecord(
            mint.toBase58(),
            [...tokenChecks, ...poolChecks],
            poolId,
            lpMint
          ),
        },
        "VERIFY_POOL"
      );
      say("ok", `Pool verification passed (${poolChecks.length}/${poolChecks.length})`);

      // Best-effort, deliberately after the on-chain work is done and verified:
      // a failed audit write must never look like a failed launch.
      const finishedRecord = captureRecord(mint.toBase58(), [...tokenChecks, ...poolChecks], poolId, lpMint);
      postAuditEvent({
        action: "launch",
        cluster: net.cluster,
        mint: mint.toBase58(),
        pool_id: poolId,
        signature: journal.sigs.CREATE_POOL,
        wallet: wallet.publicKey.toBase58(),
        detail: `${form.symbol} · ${form.totalSupply} supply`,
      });
      if (signedIn) {
        void saveCloudRecord(finishedRecord).then((id) => {
          if (id) {
            say("ok", "Rehearsal record saved to your account.");
            void listCloudRecords().then(setCloudRecords);
          }
        });
      }

      setToken({
        mint: mint.toBase58(),
        symbol: form.symbol,
        decimals: form.decimals,
        poolId: poolId!,
        lpMint: lpMint!,
      });
      setCurrent(null);
      say("ok", "Launch complete. The token is now selected across the other panels.");
    } catch (e) {
      fail(stage, e);
    } finally {
      setRunning(false);
    }
  }

  /**
   * Build the record from the verification rows that were just read off the
   * chain. Called from inside the run, while those values exist.
   */
  function captureRecord(
    mintAddress: string,
    rows: { name: string; actual: string }[],
    poolId?: string,
    lpMint?: string
  ): RehearsalRecord {
    const find = (name: string) => rows.find((c) => c.name === name)?.actual;
    return buildRecord({
      cluster: net.cluster,
      rpcUrl,
      plan: currentPlan,
      observed: {
        mint: mintAddress,
        supplyRaw: find("mint supply (raw)") ?? "",
        decimals: Number(find("mint decimals") ?? form.decimals),
        mintAuthority:
          find("mint authority after finalize") ?? find("mint authority (post-revoke)") ?? "unknown",
        freezeAuthority: find("freeze authority after finalize") ?? "unknown",
        metadataName: find("metadata name") ?? form.name,
        metadataSymbol: find("metadata symbol") ?? form.symbol,
        metadataMutable: (find("metadata mutable") ?? String(form.mutableMetadata)) === "true",
        poolId,
        lpMint,
        // The RATE, never the cluster-specific config address.
        tradeFeeRate: tier?.tradeFeeRate,
        creatorFeeRate: tier?.creatorFeeRate,
        openTimeUnix: schedule.unix,
        tokenReserveRaw: find("token reserve (raw)"),
        solReserveRaw: find("SOL reserve (raw)"),
        openingPrice: price === "—" ? undefined : price,
      },
    });
  }

  function saveRehearsal() {
    const rec = journal.rehearsalRecord;
    if (!rec) {
      say("error", "No rehearsal record was captured for this launch.");
      return;
    }
    const record = rec;
    const blob = new Blob([JSON.stringify(record, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = recordFilename(record);
    // The anchor has to be in the document, and the object URL has to outlive
    // the click: revoking it on the next line races the download and cancels
    // it, silently, on some browsers.
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      URL.revokeObjectURL(url);
      a.remove();
    }, 30_000);
    say("ok", `Rehearsal record saved as ${recordFilename(record)}`);
  }

  async function loadRehearsal(file: File) {
    await loadRehearsalRecord(await file.text());
  }

  /** One path for both sources: a record from disk and one from the account
   *  must be validated and carried identically. */
  async function loadRehearsalRecord(text: string) {
    setCarryError(null);
    const parsed = parseRecord(text);
    if ("error" in parsed) {
      setCarryError(parsed.error);
      say("error", `Rehearsal record rejected: ${parsed.error}`);
      return;
    }
    const result = carryToCluster(parsed.record, net.cluster);
    setRehearsal(parsed.record);
    setCarry(result);
    setAcknowledgedDrift(false);
    // Only the fields that survive the trip. Everything else stays as it is,
    // and the panel lists what the operator still has to decide.
    setForm((f) => ({
      ...f,
      name: result.plan.name,
      symbol: result.plan.symbol,
      decimals: result.plan.decimals,
      totalSupply: result.plan.totalSupply,
      mutableMetadata: result.plan.mutableMetadata,
      revokeMint: result.plan.revokeMint,
      revokeFreeze: result.plan.revokeFreeze,
      openPool: result.plan.openPool,
      tokenLiquidity: result.plan.tokenLiquidity,
      solLiquidity: result.plan.solLiquidity,
    }));
    setUriProbe(null);
    say(
      "ok",
      `Loaded the ${parsed.record.cluster} rehearsal of ${parsed.record.plan.symbol}. ` +
        `${result.stripped.length} fields were not carried.`
    );
  }

  function resetJournal() {
    saveJournal(rpcUrl, { ...emptyJournal });
    setJournal({ ...emptyJournal });
    setChecks([]);
    setFailed(null);
    say("info", "Cleared the launch journal. The next run starts a brand new token.");
  }

  const started = journal.done.length > 0;
  const activeStages = form.openPool ? WITH_POOL_STAGES : TOKEN_ONLY_STAGES;
  const complete = isDone(form.openPool ? "VERIFY_POOL" : "VERIFY_FINAL");

  /* Publish a read-only snapshot for the Walkthrough panel. The walkthrough
     explains this form; it does not own it, so the data flows one way and the
     dependency is a serialised string rather than the object, which would be a
     new reference on every keystroke and re-enter this effect forever. */
  const launchSnapshot: LaunchSnapshot = {
    openPool: form.openPool,
    mintChecklist: mintChecklist.map((c) => ({ label: c.label, met: c.met })),
    mintProblems,
    poolProblems,
    uriProbe,
    stagesDone: [...journal.done],
    stagesPlanned: [...activeStages],
    revokeMint: form.revokeMint,
    revokeFreeze: form.revokeFreeze,
    mutableMetadata: form.mutableMetadata,
    tierChosen: Boolean(tier),
    tierDescription: tier ? describeTier(tier) : undefined,
    openingPrice: price === "—" ? undefined : price,
    requiredLamports: requiredLamports ? requiredLamports.toNumber() : null,
    openTimeUnix: schedule.unix,
    clusterNowSec,
  };
  const launchSnapshotKey = JSON.stringify(launchSnapshot);

  useEffect(() => {
    setLaunchStatus(JSON.parse(launchSnapshotKey) as LaunchSnapshot);
  }, [launchSnapshotKey, setLaunchStatus]);

  return (
    <RequireWallet>
      <div className="card">
        <h2>Launch a token</h2>
        <p className="sub">
          Creates the mint, mints the supply and attaches Metaplex metadata — then, if you ask
          for one, opens a Raydium CPMM pool against wrapped SOL. Every write is read back off
          the chain before the next step runs.
        </p>

        {net.danger ? (
          <Note kind="danger">
            <p>
              <strong>Mainnet-beta.</strong> Every step below spends real SOL, and revoking an
              authority cannot be undone. Rehearse the whole flow on localnet or devnet first.
            </p>
          </Note>
        ) : null}

        <h3>Carry over a rehearsal</h3>
        <p className="sub" style={{ margin: "0 0 10px" }}>
          Load the record a completed launch produced on another cluster. It fills the fields that
          mean the same thing everywhere, refuses to carry the ones that do not, and keeps
          comparing what you are about to launch against what you actually tested.
        </p>
        <div className="row">
          <label className="filebtn">
            <input
              type="file"
              accept="application/json,.json"
              data-testid="in-rehearsal"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void loadRehearsal(f);
                e.target.value = "";
              }}
              disabled={started}
            />
            <span>Load rehearsal record…</span>
          </label>
          {rehearsal ? (
            <button
              className="ghost"
              data-testid="btn-clear-rehearsal"
              disabled={started}
              onClick={() => {
                setRehearsal(null);
                setCarry(null);
                setAcknowledgedDrift(false);
                say("info", "Rehearsal record unloaded. Nothing is being compared.");
              }}
            >
              Unload
            </button>
          ) : null}
        </div>
        {apiConfigured && signedIn && cloudRecords.length ? (
          <Field
            label="…or from your account"
            hint="Rehearsals saved by you or your team. Loading one behaves exactly like loading the file."
          >
            <select
              data-testid="sel-cloud-record"
              defaultValue=""
              disabled={started}
              onChange={async (e) => {
                const id = e.target.value;
                e.target.value = "";
                if (!id) return;
                const body = await getCloudRecord(id);
                if (!body) {
                  setCarryError("Could not load that record from your account.");
                  return;
                }
                await loadRehearsalRecord(JSON.stringify(body));
              }}
            >
              <option value="">Load a saved rehearsal…</option>
              {cloudRecords.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.symbol} · {r.cluster} · {new Date(r.created_at * 1000).toISOString().slice(0, 16)}
                  {r.by_email ? ` · ${r.by_email}` : ""}
                </option>
              ))}
            </select>
          </Field>
        ) : null}
        {carryError ? (
          <Note kind="danger">
            <p data-testid="rehearsal-error">{carryError}</p>
          </Note>
        ) : null}

        {rehearsal && carry ? (
          <>
            <Note kind="ok">
              <p data-testid="rehearsal-loaded">
                Rehearsed on <strong>{rehearsal.cluster}</strong> at{" "}
                {new Date(rehearsal.createdAt).toISOString().replace(".000", "")} — mint{" "}
                <code>{rehearsal.observed.mint.slice(0, 8)}…</code>, supply{" "}
                <code>{rehearsal.observed.supplyRaw}</code> raw, mint authority{" "}
                <code>{rehearsal.observed.mintAuthority}</code>.
                {rehearsal.observed.tradeFeeRate !== undefined
                  ? ` Traded at ${pct(rehearsal.observed.tradeFeeRate)}.`
                  : ""}
              </p>
            </Note>

            <h3>Not carried — you still have to decide these</h3>
            <ul className="checklist" data-testid="rehearsal-stripped">
              {carry.stripped.map((sf) => (
                <li key={sf.field} data-met="false">
                  <span className="mark">○</span>
                  <span style={{ flex: 1 }}>
                    <strong>{sf.field}</strong>
                    <div className="why">{sf.why}</div>
                  </span>
                </li>
              ))}
            </ul>
            {carry.notes.length ? (
              <Note kind="warn">
                {carry.notes.map((n, i) => (
                  <p key={i}>{n}</p>
                ))}
              </Note>
            ) : null}

            <h3>Differences from the rehearsal</h3>
            {drift.length === 0 ? (
              <Note kind="ok">
                <p data-testid="drift-none">
                  Nothing differs. What you are about to launch matches what you tested.
                </p>
              </Note>
            ) : (
              <>
                <div className="scroll" style={{ maxHeight: "none" }}>
                  <table data-testid="drift-table">
                    <thead>
                      <tr>
                        <th />
                        <th>Field</th>
                        <th>Rehearsed</th>
                        <th>Now</th>
                        <th>Why it matters</th>
                      </tr>
                    </thead>
                    <tbody>
                      {drift.map((d) => (
                        <tr key={d.field} data-severity={d.severity}>
                          <td>
                            <span className={`pill ${d.severity === "blocking" ? "danger" : "warn"}`}>
                              {d.severity === "blocking" ? "BLOCKS" : "CHECK"}
                            </span>
                          </td>
                          <td>{d.field}</td>
                          <td className="mono">{d.rehearsed}</td>
                          <td className="mono">{d.current}</td>
                          <td>{d.why}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {blocking.length ? (
                  <div className="row" style={{ marginTop: 10 }}>
                    <label>
                      <input
                        type="checkbox"
                        data-testid="in-ackdrift"
                        checked={acknowledgedDrift}
                        onChange={(e) => setAcknowledgedDrift(e.target.checked)}
                      />
                      I have reviewed {blocking.length} difference
                      {blocking.length === 1 ? "" : "s"} from the rehearsal and intend{" "}
                      {blocking.length === 1 ? "it" : "them"}
                    </label>
                  </div>
                ) : null}
              </>
            )}
          </>
        ) : null}

        <h3>What this run will do</h3>
        <div className="row">
          <label>
            <input
              type="checkbox"
              data-testid="in-openpool"
              checked={form.openPool}
              onChange={(e) => set("openPool", e.target.checked)}
              disabled={started}
            />
            Also open a Raydium CPMM pool against wrapped SOL
          </label>
        </div>
        <p className="sub" style={{ margin: "6px 0 0" }}>
          {form.openPool
            ? "Six stages: mint, metadata, verify, revoke authorities, create pool, verify pool."
            : "Five stages: mint, metadata, verify, revoke authorities, verify again. The token will exist but will not be tradeable until a pool exists."}
          {started ? " Locked for this run — clear the journal to change it." : ""}
        </p>

        <h3>Minimum to mint a coin</h3>
        <p className="sub" style={{ margin: "0 0 10px" }}>
          These six are the whole requirement. Everything below them is either a
          pool input or a choice you could make differently and still end up with a
          token.
        </p>
        <ul className="checklist" data-testid="mint-checklist">
          {mintChecklist.map((item) => (
            <li
              key={item.label}
              data-met={item.pending ? "pending" : item.met}
              data-testid={`req-${item.label}`}
            >
              <span className="mark">{item.pending ? "…" : item.met ? "✓" : "○"}</span>
              <span style={{ flex: 1 }}>
                <strong>{item.label}</strong>
                <div className="why">{item.why}</div>
              </span>
            </li>
          ))}
        </ul>

        <h3>Token</h3>
        <div className="grid">
          <Field label="Token name" req="mint" hint="1–32 characters, stored on-chain">
            <input
              data-testid="in-name"
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              placeholder="Example Coin"
            />
          </Field>
          <Field label="Symbol" req="mint" hint="1–10 characters">
            <input
              data-testid="in-symbol"
              value={form.symbol}
              onChange={(e) => set("symbol", e.target.value)}
              placeholder="EXM"
            />
          </Field>
          <Field label="Decimals" req="mint" hint="9 matches SOL; 6 matches USDC. Fixed at creation.">
            <input
              data-testid="in-decimals"
              type="number"
              min={0}
              max={9}
              value={form.decimals}
              onChange={(e) => set("decimals", Number(e.target.value))}
            />
          </Field>
          <Field label="Total supply" req="mint" hint="Minted once, in full, to your wallet">
            <input
              data-testid="in-supply"
              value={form.totalSupply}
              onChange={(e) => set("totalSupply", e.target.value)}
            />
          </Field>
        </div>

        <Field
          label="Metadata JSON URI"
          req="mint"
          hint="Host it somewhere permanent (IPFS/Arweave). If this 404s, wallets show a blank square — and permanently so if metadata is immutable."
        >
          <div className="row">
            <input
              data-testid="in-uri"
              style={{ flex: 1 }}
              value={form.metadataUri}
              onChange={(e) => {
                // No flag to maintain: the moment this differs from what the
                // component wrote, it is the operator's value by definition.
                set("metadataUri", e.target.value);
                setUriProbe(null);
              }}
              placeholder="https://…/metadata.json"
            />
            <button
              onClick={async () => setUriProbe(await probeMetadataUri(form.metadataUri))}
              disabled={!form.metadataUri}
            >
              Test URI
            </button>
          </div>
        </Field>
        {uriAutoFilled ? (
          <Note kind="ok">
            <p data-testid="uri-autofilled">
              Filled in with the rehearsal document this console serves, so a devnet run is
              not blocked on hosting a file. It is cleared automatically if you switch to
              mainnet — that document describes a rehearsal token, and metadata is written
              before authorities are revoked, so it could not be corrected afterwards.
            </p>
          </Note>
        ) : null}
        <Field
          label="Image URL"
          hint="Optional. Used only when you build a metadata document below; empty falls back to this console's logo."
        >
          <input
            data-testid="in-image"
            value={form.imageUrl}
            onChange={(e) => set("imageUrl", e.target.value)}
            placeholder="https://…/logo.png"
          />
        </Field>
        <CoinImage name={form.name} symbol={form.symbol} imageUrl={form.imageUrl} />
        <div className="row">
          <button
            data-testid="btn-build-metadata"
            onClick={downloadMetadataDoc}
            disabled={!form.name || !form.symbol}
          >
            Build metadata document from these fields
          </button>
        </div>
        {uriProbe ? (
          <Note kind={uriProbe.ok ? "ok" : "danger"}>
            <p>
              {uriProbe.ok ? "URI resolves" : "URI does not resolve"} — {uriProbe.detail}
            </p>
          </Note>
        ) : null}

        {form.openPool ? (
          <>
        <h3>Pool</h3>
        <div className="grid">
          <Field label={`Tokens into the pool (${form.symbol || "token"})`} req="pool">
            <input
              data-testid="in-tokenliq"
              value={form.tokenLiquidity}
              onChange={(e) => set("tokenLiquidity", e.target.value)}
            />
          </Field>
          <Field label="SOL into the pool" req="pool">
            <input
              data-testid="in-solliq"
              value={form.solLiquidity}
              onChange={(e) => set("solLiquidity", e.target.value)}
            />
          </Field>
        </div>

        <h3>Trading window</h3>
        <div className="row">
          <label>
            <input
              type="checkbox"
              data-testid="in-schedule"
              checked={form.openAt !== ""}
              onChange={(e) =>
                set("openAt", e.target.checked ? unixToLocalInput(nowSec + 3600) : "")
              }
              disabled={started}
            />
            Open trading at a specific time instead of immediately
          </label>
        </div>
        {form.openAt !== "" ? (
          <>
            <Field
              label="Trading opens"
              req="pool"
              hint="Your local time. The pool is created now and verified now; the cp-swap program itself refuses swaps until the cluster clock passes this moment."
            >
              <input
                type="datetime-local"
                data-testid="in-openat"
                value={form.openAt}
                onChange={(e) => set("openAt", e.target.value)}
                disabled={started}
              />
            </Field>
            <Note kind={scheduleWarnings.length ? "warn" : undefined}>
              <p data-testid="schedule-summary">
                Opens <strong>{describeOpenTime(schedule.unix)}</strong>
              </p>
              <p>
                Cluster clock is{" "}
                {clusterNowSec === null
                  ? "unavailable — falling back to this machine's clock, which the program does not use"
                  : new Date(clusterNowSec * 1000).toISOString().replace(".000", "")}
                . This time is stored on chain, so it holds whether or not anything of yours is
                running — and it cannot be changed afterwards.
              </p>
              {scheduleWarnings.map((w, i) => (
                <p key={i}>{w.message}</p>
              ))}
            </Note>
          </>
        ) : null}

        <Field
          label="Fee tier"
          req="pool"
          hint="Read live from the chain. Never chosen by index — index N is a different tier on devnet than on mainnet."
        >
          <select
            data-testid="in-tier"
            value={form.feeTierId}
            onChange={(e) => set("feeTierId", e.target.value)}
          >
            <option value="">Select a fee tier…</option>
            {tiers
              .slice()
              .sort((a, b) => a.tradeFeeRate - b.tradeFeeRate || a.index - b.index)
              .map((t) => (
                <option key={t.id} value={t.id}>
                  {describeTier(t)}
                </option>
              ))}
          </select>
        </Field>
        {tiersError ? <Note kind="danger"><p>{tiersError}</p></Note> : null}
        {tier ? (
          <Note>
            <p>
              LP trade fee <strong>{pct(tier.tradeFeeRate)}</strong>. Configured creator fee{" "}
              <strong>{pct(tier.creatorFeeRate)}</strong>, which a pool created this way does{" "}
              <strong>not</strong> collect — <code>createPool</code> leaves{" "}
              <code>enableCreatorFee</code> false. Traders pay the trade fee only. Pool creation
              costs {fromBaseUnits(new BN(tier.createPoolFeeLamports), 9)} SOL.
            </p>
          </Note>
        ) : null}
          </>
        ) : null}

        <h3>Authorities and metadata mutability</h3>
        <p className="sub" style={{ margin: "0 0 10px" }}>
          None of these three is required to mint. All three are permanent the
          moment the transaction confirms, and there is no recovery path for any
          of them — not by you, not by Raydium, not by Solana.
        </p>
        <div className="row">
          <label>
            <input
              type="checkbox"
              data-testid="in-revokemint"
              checked={form.revokeMint}
              onChange={(e) => set("revokeMint", e.target.checked)}
            />
            Revoke mint authority (fixes supply forever)
          </label>
          <Req kind="irreversible" />
        </div>
        <div className="row" style={{ marginTop: 6 }}>
          <label>
            <input
              type="checkbox"
              data-testid="in-revokefreeze"
              checked={form.revokeFreeze}
              onChange={(e) => set("revokeFreeze", e.target.checked)}
            />
            Revoke freeze authority (nobody can freeze holder accounts)
          </label>
          <Req kind="irreversible" />
        </div>
        <div className="row" style={{ marginTop: 6 }}>
          <label>
            <input
              type="checkbox"
              checked={form.mutableMetadata}
              onChange={(e) => set("mutableMetadata", e.target.checked)}
            />
            Metadata stays mutable (uncheck to freeze name, symbol and URI permanently)
          </label>
          <Req kind={form.mutableMetadata ? "optional" : "irreversible"} />
        </div>

        <h3>Plan</h3>
        <div className="grid">
          {form.openPool ? (
            <>
              <Stat label="Opening price" value={`${price} SOL`} />
              <Stat
                label="Pooled share of supply"
                value={
                  supplyRaw && tokenLiqRaw && !supplyRaw.isZero()
                    ? `${tokenLiqRaw.muln(10000).div(supplyRaw).toNumber() / 100}%`
                    : "—"
                }
              />
            </>
          ) : (
            <Stat label="Pool" value="none" />
          )}
          <Stat
            label={form.openPool ? "SOL needed (min)" : "SOL needed (est.)"}
            value={requiredLamports ? `${fromBaseUnits(requiredLamports, 9)}` : "—"}
          />
          <Stat
            label="Wallet balance"
            value={balance === null ? "—" : `${fromBaseUnits(new BN(balance), 9)} SOL`}
          />
        </div>

        {underfunded ? (
          <Note kind="danger">
            <p>
              Wallet holds less than this run needs. Fund it before starting — a run that dies
              part-way through leaves a token that is missing something permanent.
            </p>
          </Note>
        ) : null}

        {problems.length ? (
          <Note kind="warn">
            {problems.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </Note>
        ) : null}

        <div className="row" style={{ marginTop: 16 }}>
          <button
            className="primary"
            data-testid="btn-launch"
            disabled={running || complete || problems.length > 0 || underfunded}
            onClick={run}
          >
            {running
              ? "Running…"
              : complete
                ? "Launch complete"
                : started
                  ? "Resume launch"
                  : "Launch"}
          </button>
          {complete ? (
            <button
              onClick={saveRehearsal}
              disabled={!journal.rehearsalRecord}
              title={
                journal.rehearsalRecord
                  ? "What the chain reported, captured when the launch finished"
                  : "No record was captured for this launch"
              }
              data-testid="btn-save-rehearsal"
            >
              Save rehearsal record
            </button>
          ) : null}
          {started ? (
            <button
              className={complete ? "primary" : "ghost"}
              onClick={resetJournal}
              disabled={running}
              data-testid="btn-newtoken"
            >
              Start a new token
            </button>
          ) : null}
        </div>
      </div>

      {started || running ? (
        <div className="card">
          <h2>Progress</h2>
          {journal.mint ? (
            <dl className="kv" style={{ marginBottom: 14 }}>
              <dt>Mint</dt>
              <dd data-testid="out-mint">
                <Addr value={journal.mint} />
              </dd>
              {journal.poolId ? (
                <>
                  <dt>Pool</dt>
                  <dd data-testid="out-pool">
                    <Addr value={journal.poolId} />
                  </dd>
                </>
              ) : null}
            </dl>
          ) : null}
          <ul className="stages">
            {activeStages.map((s) => (
              <li key={s}>
                <i
                  className={`dot ${
                    failed === s ? "failed" : isDone(s) ? "done" : current === s ? "active" : ""
                  }`}
                />
                <span style={{ flex: 1 }}>{s}</span>
                {journal.sigs[s] ? <Addr value={journal.sigs[s]} kind="tx" /> : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {checks.length ? (
        <div className="card">
          <h2>Verification</h2>
          <p className="sub">
            {checks.filter((x) => x.pass).length}/{checks.length} checks passed. Every value was
            read back from the chain after it was written.
          </p>
          <div className="scroll">
            <table data-testid="checks">
              <thead>
                <tr>
                  <th />
                  <th>Check</th>
                  <th className="mono">Expected</th>
                  <th className="mono">Actual</th>
                </tr>
              </thead>
              <tbody>
                {checks.map((x, i) => (
                  <tr key={i}>
                    <td>
                      <span className={`pill ${x.pass ? "ok" : "danger"}`}>
                        {x.pass ? "PASS" : "FAIL"}
                      </span>
                    </td>
                    <td>{x.name}</td>
                    <td className="mono">{x.expected}</td>
                    <td className="mono">{x.actual}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </RequireWallet>
  );
}

function c(name: string, expected: unknown, actual: unknown) {
  return {
    name,
    expected: String(expected),
    actual: String(actual),
    pass: String(expected) === String(actual),
  };
}
